import { useState } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Box,
  Divider,
  Drawer,
  IconButton,
  Skeleton,
  Theme,
  Tooltip,
  Typography,
} from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import moment from 'moment';

import { StatusChip } from '../../components/StatusChip';
import FormLinkButton from '../../components/buttons/FormLinkButton';
import { numberToUSDCurrency } from '../../helpers/numberToUSDCurrency';
import { palette } from '../../palette';
import OwnerBadge from './OwnerBadge';
import StageProgress from './StageProgress';
import StatusBadge, { getPayoutStatusColor } from './StatusBadge';
import Timeline from './Timeline';
import { PayoutTimelineEvent, PayoutWorklistDetailResponse } from './types';

interface PayoutDrawerProps {
  open: boolean;
  loading: boolean;
  data: PayoutWorklistDetailResponse | null;
  onClose: () => void;
  showOwner?: boolean;
}

const useStyles = makeStyles()((theme: Theme) => ({
  paper: {
    width: '460px',
    maxWidth: '100vw',
    backgroundColor: theme.palette.primary.main,
    borderLeft: `1px solid ${theme.palette.secondary.dark}`,
    color: theme.palette.common.white,
    padding: '20px',
    boxSizing: 'border-box',

    '@media (max-width: 600px)': {
      width: '100vw',
    },
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '16px',
  },
  title: {
    fontFamily: 'Poppins',
    fontSize: '24px',
    fontWeight: 600,
  },
  section: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
    marginBottom: '14px',
  },
  label: {
    color: theme.palette.text.secondary,
    fontFamily: 'Inter',
    fontSize: '12px',
  },
  value: {
    color: theme.palette.common.white,
    fontFamily: 'Inter',
    fontSize: '14px',
  },
  valueWithLink: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
  },
  inlineLinkFix: {
    display: 'inline-flex',
    alignItems: 'center',
    lineHeight: 1,

    '& a': {
      marginBottom: 0,
      display: 'inline-flex',
      alignItems: 'center',
    },

    '& .MuiIconButton-root': {
      paddingBottom: 0,
    },
  },
  caseRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '10px',
  },
  timelineTitle: {
    color: theme.palette.common.white,
    fontFamily: 'Poppins',
    fontSize: '18px',
    fontWeight: 600,
  },
  collapsibleHeader: {
    marginTop: '8px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    cursor: 'pointer',
    userSelect: 'none',
  },
  requestsHistoryList: {
    marginTop: '12px',
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  requestsHistoryRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  requestsHistoryCounter: {
    color: theme.palette.text.secondary,
    fontFamily: 'Inter',
    fontSize: '12px',
    minWidth: '16px',
  },
  requestsHistoryTime: {
    color: theme.palette.common.white,
    fontFamily: 'Inter',
    fontSize: '13px',
  },
  activeIndicator: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    marginLeft: '6px',
  },
  activeDot: {
    width: '8px',
    height: '8px',
    borderRadius: '50%',
    backgroundColor: '#88B856',
    display: 'inline-block',
  },
  activeLabel: {
    color: '#88B856',
    fontFamily: 'Inter',
    fontSize: '12px',
    fontWeight: 600,
  },
}));

const toTitle = (value: string) =>
  value
    .replace(/_/g, ' ')
    .toLowerCase()
    .replace(/\b\w/g, (char) => char.toUpperCase());

const getOnboardingHistoryTitle = (event: string, toState: string) => {
  const normalizedEvent = event.trim().toUpperCase().replace(/\s+/g, '_');
  const normalizedToState = toState.trim().toUpperCase();

  if (normalizedEvent === 'SETTLEMENT_FUNDING_INITIATED' && normalizedToState === 'COMPLETE') {
    return 'Disbursement Submitted';
  }

  if (normalizedEvent === 'SETTLEMENT_FUNDING_INITIATED' && normalizedToState === 'SETTLEMENT_PROCESSING') {
    return;
  }

  if (normalizedEvent === 'ADMIN_INITIATE' && normalizedToState === 'LINK_DELIVERY') {
    return 'Link Delivery Initiated';
  }

  return toTitle(event);
};

const getOnboardingProgressEvents = (detail: PayoutWorklistDetailResponse | null): PayoutTimelineEvent[] => {
  if (!detail?.onboardingProgress?.steps?.length) {
    return [];
  }

  return detail.onboardingProgress.steps.map((step) => {
    const displayStatus =
      typeof step.failureType === 'string' && step.failureType.trim().length > 0
        ? toTitle(step.failureType.trim())
        : step.status;

    return {
      title: step.label || step.step || 'Step',
      timestamp: step.dateTime || step.datetime || null,
      metadata: step.message || displayStatus,
      status: displayStatus,
    };
  });
};

const getOnboardingHistoryEvents = (detail: PayoutWorklistDetailResponse | null): PayoutTimelineEvent[] => {
  if (!detail?.onboardingHistory?.length) {
    return [];
  }

  return detail.onboardingHistory.reduce<PayoutTimelineEvent[]>((events, item) => {
    const title = getOnboardingHistoryTitle(item.event, item.toState);

    if (!title) {
      return events;
    }

    events.push({
      title,
      timestamp: item.createdAt,
      metadata: `${toTitle(item.fromState)} -> ${toTitle(item.toState)}`,
      status: 'Completed',
      icon: 'greenDot',
    });

    return events;
  }, []);
};

const getPayoutProgressEvents = (detail: PayoutWorklistDetailResponse | null): PayoutTimelineEvent[] => {
  if (!detail?.payoutProgress?.steps?.length) {
    return [];
  }

  return detail.payoutProgress.steps.map((step) => ({
    title: step.step || step.label || 'Step',
    timestamp: step.datetime || step.dateTime || null,
    metadata: step.errorMessage || null,
    status: step.status,
    claimantStep: step.claimantStep || null,
  }));
};

const PayoutDrawer = ({ open, loading, data, onClose, showOwner = true }: PayoutDrawerProps) => {
  const { classes } = useStyles();
  const waveLabel = data?.settlement?.label?.trim().toUpperCase() || '';
  const sequenceNo = data?.settlement?.sequenceNo;
  const isWaveTwoOrMore = typeof sequenceNo === 'number'
    ? sequenceNo > 1
    : /^W\d+$/.test(waveLabel) && waveLabel !== 'W1';
  const [expandedSections, setExpandedSections] = useState({
    onboardingProgress: false,
    payoutProgress: false,
    onboardingHistory: false,
    onboardingRequestsHistory: false,
  });
  const onboardingProgressEvents = isWaveTwoOrMore ? [] : getOnboardingProgressEvents(data);
  const payoutProgressEvents = getPayoutProgressEvents(data);
  const onboardingHistoryEvents = isWaveTwoOrMore ? [] : getOnboardingHistoryEvents(data);
  const onboardingRequestsHistory = data?.onboardingRequests || [];
  const visibleTimelineSections = [
    onboardingProgressEvents.length > 0,
    payoutProgressEvents.length > 0,
    onboardingHistoryEvents.length > 0,
    onboardingRequestsHistory.length > 0,
  ].filter(Boolean).length;

  const claimantName = data?.claimant?.name || '-';
  const claimantReferenceId = data?.claimant?.referenceId;
  const clientId = data?.claimant?.clientId;
  const caseId = data?.case?.id;
  const caseNumber = data?.case?.caseNumber || data?.caseNumber || '-';
  const settlementReference =
    data?.settlement?.settlementReference || data?.settlementReference || '-';
  const settlementId = data?.settlement?.settlementId || data?.settlementId;
  const paymentMethod = data?.payment?.paymentMethod || data?.paymentMethod || 'N/A';
  const paymentTraceNumber = data?.payment?.traceNumber;
  const paymentCheckNumber = data?.payment?.checkNumber;
  const normalizedPaymentMethod = paymentMethod.trim().toUpperCase();
  const paymentReferenceLabel = normalizedPaymentMethod === 'ACH' ? 'Trace Number' : 'Check Number';
  const paymentReferenceValue = normalizedPaymentMethod === 'ACH'
    ? paymentTraceNumber
    : paymentCheckNumber;
  const status = data?.status || 'NOT_STARTED';
  const detailsStatusColor = getPayoutStatusColor(status);
  const amount =
    typeof data?.settlement?.amount === 'number'
      ? data.settlement.amount
      : typeof data?.amount === 'number'
        ? data.amount
        : 0;
  const updatedAt = data?.updatedAt;

  const toggleSection = (
    section: 'onboardingProgress' | 'payoutProgress' | 'onboardingHistory' | 'onboardingRequestsHistory',
  ) => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{ className: classes.paper, role: 'dialog', 'aria-label': 'Payout details drawer' }}
    >
      <Box className={classes.header}>
        <Typography className={classes.title}>Payout Details</Typography>
        <IconButton onClick={onClose} aria-label="Close payout drawer">
          <CloseIcon htmlColor="#FFF" />
        </IconButton>
      </Box>

      {loading && (
        <Box display="flex" flexDirection="column" gap="10px">
          <Skeleton variant="rounded" height={34} />
          <Skeleton variant="rounded" height={26} />
          <Skeleton variant="rounded" height={26} />
          <Skeleton variant="rounded" height={80} />
          <Skeleton variant="rounded" height={140} />
        </Box>
      )}

      {!loading && data && (
        <>
          <Box className={classes.section}>
            <Typography className={classes.label}>Claimant</Typography>
            <Box>
              <Box>
                <Box className={classes.valueWithLink}>
                  <Typography className={classes.value}>{claimantName}</Typography>
                  {!!clientId && (
                    <Box className={classes.inlineLinkFix}>
                      <FormLinkButton path={`/clients/${clientId}/home`} />
                    </Box>
                  )}
                </Box>
                <Typography className={classes.label}>Reference ID: {claimantReferenceId ?? '-'}</Typography>
              </Box>
            </Box>
          </Box>

          <Box className={classes.section}>
            <Typography className={classes.label}>Case</Typography>
            <Box className={classes.valueWithLink}>
              <Typography className={classes.value}>{caseNumber}</Typography>
              {!!caseId && (
                <Box className={classes.inlineLinkFix}>
                  <FormLinkButton path={`/cases/${caseId}/home`} />
                </Box>
              )}
            </Box>
          </Box>

          <Box className={classes.section}>
            <Typography className={classes.label}>Settlement</Typography>
            <Box display="flex" flexDirection="column" gap="6px">
              <Box className={classes.valueWithLink}>
                <Typography className={classes.value}>{settlementReference}</Typography>
                {!!settlementId && (
                  <Box className={classes.inlineLinkFix}>
                    <FormLinkButton path={`/payments/settlement/${settlementId}`} />
                  </Box>
                )}
              </Box>
              {!!waveLabel && <StatusChip label={waveLabel} color={palette.info.extralight} />}
            </Box>
          </Box>

          <Box className={classes.section}>
            <Typography className={classes.label}>Method</Typography>
            <Box display="flex" flexDirection="column" gap="6px">
              <StatusChip label={paymentMethod} color={detailsStatusColor} />
              {!!paymentReferenceValue && (
                <Typography className={classes.value}>
                  {paymentReferenceLabel}: {paymentReferenceValue}
                </Typography>
              )}
            </Box>
          </Box>

          <Box className={classes.section}>
            <Typography className={classes.label}>Amount</Typography>
            <Typography className={classes.value}>{numberToUSDCurrency(amount)}</Typography>
          </Box>

          <Box className={classes.section}>
            <Typography className={classes.label}>Stage</Typography>
            <StageProgress stage={data.stage} />
          </Box>

          {!isWaveTwoOrMore && (
            <Box className={classes.section}>
              <Typography className={classes.label}>Onboarding Status</Typography>
              <Box>
                <StatusBadge status={status} />
              </Box>
            </Box>
          )}

          {showOwner && (
            <Box className={classes.section}>
              <Typography className={classes.label}>Owner</Typography>
              <Box>
                <OwnerBadge owner={data.owner || 'Info'} color={detailsStatusColor} />
              </Box>
            </Box>
          )}

          <Box className={classes.section}>
            <Typography className={classes.label}>Updated</Typography>
            <Tooltip title={updatedAt ? moment(updatedAt).format('MMM D, YYYY h:mm:ss A') : '-'}>
              <Typography className={classes.value}>{updatedAt ? moment(updatedAt).fromNow() : '-'}</Typography>
            </Tooltip>
          </Box>

          {visibleTimelineSections > 0 && (
            <Divider sx={{ borderColor: 'secondary.dark', margin: '6px 0 10px' }} />
          )}

          {!!onboardingProgressEvents.length && (
            <>
              <Box
                className={classes.collapsibleHeader}
                onClick={() => toggleSection('onboardingProgress')}
                role="button"
                aria-label="Toggle onboarding progress section"
              >
                <Typography className={classes.timelineTitle}>Onboarding Progress</Typography>
                {expandedSections.onboardingProgress ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </Box>
              {expandedSections.onboardingProgress && <Timeline events={onboardingProgressEvents} />}
            </>
          )}

          {!!payoutProgressEvents.length && (
            <>
              {!!onboardingProgressEvents.length && <Divider sx={{ borderColor: 'secondary.dark', margin: '12px 0 10px' }} />}
              <Box
                className={classes.collapsibleHeader}
                onClick={() => toggleSection('payoutProgress')}
                role="button"
                aria-label="Toggle payout progress section"
              >
                <Typography className={classes.timelineTitle}>Payout Progress</Typography>
                {expandedSections.payoutProgress ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </Box>
              {expandedSections.payoutProgress && <Timeline events={payoutProgressEvents} />}
            </>
          )}

          {!!onboardingHistoryEvents.length && (
            <>
              {(!!onboardingProgressEvents.length || !!payoutProgressEvents.length)
                && <Divider sx={{ borderColor: 'secondary.dark', margin: '12px 0 10px' }} />}
              <Box
                className={classes.collapsibleHeader}
                onClick={() => toggleSection('onboardingHistory')}
                role="button"
                aria-label="Toggle onboarding history section"
              >
                <Typography className={classes.timelineTitle}>Onboarding History</Typography>
                {expandedSections.onboardingHistory ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </Box>
              {expandedSections.onboardingHistory && <Timeline events={onboardingHistoryEvents} />}
            </>
          )}

          {!!onboardingRequestsHistory.length && (
            <>
              {(!!onboardingProgressEvents.length || !!payoutProgressEvents.length || !!onboardingHistoryEvents.length)
                && <Divider sx={{ borderColor: 'secondary.dark', margin: '12px 0 10px' }} />}
              <Box
                className={classes.collapsibleHeader}
                onClick={() => toggleSection('onboardingRequestsHistory')}
                role="button"
                aria-label="Toggle onboarding requests history section"
              >
                <Typography className={classes.timelineTitle}>Onboarding Request History</Typography>
                {expandedSections.onboardingRequestsHistory ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </Box>
              {expandedSections.onboardingRequestsHistory && (
                <Box className={classes.requestsHistoryList}>
                  {onboardingRequestsHistory.map((request, index) => (
                    <Box
                      key={`${request.onboardingId || request.refId || 'request'}-${request.timestamp || index}`}
                      className={classes.requestsHistoryRow}
                    >
                      <Typography className={classes.requestsHistoryCounter}>{index + 1}.</Typography>
                      <Typography className={classes.requestsHistoryTime}>
                        {request.timestamp ? moment(request.timestamp).format('MMM D, YYYY h:mm:ss A') : '-'}
                      </Typography>
                      {request.isActive && (
                        <Box className={classes.activeIndicator}>
                          <Box className={classes.activeDot} />
                          <Typography className={classes.activeLabel}>Active</Typography>
                        </Box>
                      )}
                    </Box>
                  ))}
                </Box>
              )}
            </>
          )}
        </>
      )}
    </Drawer>
  );
};

export default PayoutDrawer;
